import type React from 'react';

import {
  Box,
  Divider,
  FieldSet,
  FormField,
  FormHelp,
  Input,
  Label,
  LabeledControl,
  Legend,
  Switch,
} from '@zillow/constellation-10';

import type { Section } from '../../../../models/index';
import { WATER_SEWER } from '../../../../constants';
import HeaderToggle from './HeaderToggle';
import FixedOrVariableSelector from './FixedOrVariableSelector';
import { isMultiFamily } from './MultiFamilyLandlordUtility';
import RadioListBox from '../../../Widgets/RadioListBox';
import { UTILITIES_HEADING_AND_CONTENT } from '../../../../constants/stringConstants';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

const INITIAL_STATE = {
  [WATER_SEWER.FIELD_NAME.IS_ENABLED]: true,
  [WATER_SEWER.FIELD_NAME.FIXED_AMOUNT_SELECTED]: false,
  [WATER_SEWER.FIELD_NAME.FIXED_AMOUNT_VALUE]: null,
  [WATER_SEWER.FIELD_NAME.VARIABLE_COST_SELECTED]: false,
  [WATER_SEWER.FIELD_NAME.SERVICE_PROVIDER]: '',
  [WATER_SEWER.FIELD_NAME.WATER_SUBMETER]: null,
  [WATER_SEWER.FIELD_NAME.WATER_SUBMETER_LOCATION]: '',
};

export default function WaterSewer_v4({ section, setSectionValues }: Props) {
  const option1 = WATER_SEWER.FIELD_NAME.TENANT_RESPONSIBILITY;
  const option2 = WATER_SEWER.FIELD_NAME.LANDLORD_RESPONSIBILITY;
  const waterSewer_isEnabled = section.getFieldValue(WATER_SEWER.FIELD_NAME.IS_ENABLED);
  const option1Selected = section.getFieldValue(option1);
  const option2Selected = section.getFieldValue(option2);
  const waterSubmeter_isEnabled = section.getFieldValue(WATER_SEWER.FIELD_NAME.WATER_SUBMETER);

  function setAdditionalFixedAmountValue(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    setSectionValues({
      [WATER_SEWER.FIELD_NAME.FIXED_AMOUNT_VALUE]: event.currentTarget.value,
    });
  }

  function setAdditionalFixedAmountSelected(): void {
    setSectionValues({
      [WATER_SEWER.FIELD_NAME.FIXED_AMOUNT_SELECTED]: true,
      [WATER_SEWER.FIELD_NAME.VARIABLE_COST_SELECTED]: false,
    });
  }

  function setServiceProvider(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    setSectionValues({
      [WATER_SEWER.FIELD_NAME.SERVICE_PROVIDER]: event.currentTarget.value,
    });
  }

  function toggleUtility(): void {
    setSectionValues({
      ...INITIAL_STATE,
      [WATER_SEWER.FIELD_NAME.IS_ENABLED]: !section.getFieldValue(WATER_SEWER.FIELD_NAME.IS_ENABLED),
    });
  }

  function setAdditionalVariableCostSelected(): void {
    setSectionValues({
      [WATER_SEWER.FIELD_NAME.FIXED_AMOUNT_VALUE]: '',
      [WATER_SEWER.FIELD_NAME.FIXED_AMOUNT_SELECTED]: false,
      [WATER_SEWER.FIELD_NAME.VARIABLE_COST_SELECTED]: true,
    });
  }

  function renderOption2Details(): React.ReactNode {
    return (
      <FixedOrVariableSelector
        legendText="What is the additional fee for water/sewer?"
        fixedAmountSelected={section.getFieldValue(WATER_SEWER.FIELD_NAME.FIXED_AMOUNT_SELECTED)}
        fixedAmountValue={section.getFieldValue(WATER_SEWER.FIELD_NAME.FIXED_AMOUNT_VALUE)}
        setFixedAmountSelected={setAdditionalFixedAmountSelected}
        setFixedAmountValue={setAdditionalFixedAmountValue}
        variableMonthlyCostSelected={section.getFieldValue(WATER_SEWER.FIELD_NAME.VARIABLE_COST_SELECTED)}
        setVariableMonthlySelected={setAdditionalVariableCostSelected}
      />
    );
  }

  function renderOption1Details(): React.ReactNode {
    return (
      <FormField
        required
        label={<Label>What service provider?</Label>}
        control={
          <Input
            data-test="service-provider"
            value={section.getFieldValue(WATER_SEWER.FIELD_NAME.SERVICE_PROVIDER)}
            onChange={setServiceProvider}
          />
        }
        description={
          <FormHelp>Hot water costs may be included in electricity or natural gas costs as applicable</FormHelp>
        }
      />
    );
  }

  function onWaterSubmeterLocationChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [WATER_SEWER.FIELD_NAME.WATER_SUBMETER_LOCATION]: value,
    });
  }

  function onSelectWaterSubmeter(): void {
    setSectionValues({
      [WATER_SEWER.FIELD_NAME.WATER_SUBMETER]: !section.getFieldValue(WATER_SEWER.FIELD_NAME.WATER_SUBMETER),
      [WATER_SEWER.FIELD_NAME.FIXED_AMOUNT_SELECTED]: false,
      [WATER_SEWER.FIELD_NAME.VARIABLE_COST_SELECTED]: true,
      [WATER_SEWER.FIELD_NAME.FIXED_AMOUNT_SELECTED]: false,
      [WATER_SEWER.FIELD_NAME.FIXED_AMOUNT_VALUE]: null,
      [WATER_SEWER.FIELD_NAME.VARIABLE_COST_SELECTED]: false,
      [WATER_SEWER.FIELD_NAME.SERVICE_PROVIDER]: '',
      [WATER_SEWER.FIELD_NAME.TENANT_RESPONSIBILITY]: false,
      [WATER_SEWER.FIELD_NAME.LANDLORD_RESPONSIBILITY]: false,
    });
  }

  const isMultiFamilyLandlord = isMultiFamily(section);

  isMultiFamilyLandlord ? (section.showMandate = true) : (section.showMandate = false);

  const showOptionSelector = isMultiFamilyLandlord
    ? waterSubmeter_isEnabled !== null && waterSewer_isEnabled && !waterSubmeter_isEnabled
    : waterSewer_isEnabled;

  return (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: 'layout.tighter' }}>
      <HeaderToggle
        header="Water/sewer"
        subheader="Tenant pays in addition to base rent"
        switchOn={waterSewer_isEnabled}
        onSwitchToggle={toggleUtility}
      />
      {isMultiFamilyLandlord && waterSewer_isEnabled && (
        <>
          <Divider css={{ marginTop: 'layout.tighter', marginBottom: 'layout.tighter' }} />
          <LabeledControl
            labelPosition="left"
            fluid
            size="lg"
            label={<Label>Water submeter in unit</Label>}
            control={
              <Switch
                onChange={onSelectWaterSubmeter}
                checked={section.getFieldValue(WATER_SEWER.FIELD_NAME.WATER_SUBMETER) ?? false}
              />
            }
          />
          {section.getFieldValue(WATER_SEWER.FIELD_NAME.WATER_SUBMETER) && (
            <FormField
              required
              label={<Label>Where is the water submeter?</Label>}
              control={
                <Input
                  aria-labelledby="water-submeter-location"
                  value={section.getFieldValue(WATER_SEWER.FIELD_NAME.WATER_SUBMETER_LOCATION)}
                  onChange={onWaterSubmeterLocationChange}
                />
              }
            />
          )}
        </>
      )}
      {showOptionSelector && (
        <>
          <Divider css={{ marginTop: 'layout.tighter', marginBottom: 'layout.tighter' }} />
          <Box css={{ marginBottom: 'layout.tight' }}>
            <FieldSet required legend={<Legend>Who will be responsible for water/sewer set up?</Legend>}>
              <RadioListBox
                section={section}
                setSectionValues={setSectionValues}
                option1FieldName={option1}
                option2FieldName={option2}
                option1Header={UTILITIES_HEADING_AND_CONTENT.option1Heading}
                option1Content={UTILITIES_HEADING_AND_CONTENT.option1Content}
                option2Header={UTILITIES_HEADING_AND_CONTENT.option2Heading}
                option2Content={UTILITIES_HEADING_AND_CONTENT.option2Content}
              />
            </FieldSet>
          </Box>
          {option1Selected && renderOption1Details()}
          {option2Selected && renderOption2Details()}
        </>
      )}
    </Box>
  );
}

WaterSewer_v4.description = 'Version 1 with the additional water submeter.';
